/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Icon, SubTitle, Title, TitleWrapper } from './PageTitle.styles';

import { Icons } from '../../../Constants/Icons';
import Button, { ButtonType, IconDirection } from '../Buttons/Button';

import { Paths, USER_PROCESSES } from '../../../Constants/App';
import AsyncStorageKeys from '../../../Constants/StorageKeys';
import { Box, CircularProgress } from '@mui/material';
import theme from '../../../Themes/theme';
import { ConversationName } from '../../Conversations/ConversationsScreen/Conversations.Style';
import { getProcessData, hasAccessToConversations } from '../../../Utilities/utils';

interface IPageTitleProps {
  title: string;
  Loader?: boolean;
  showJobIcon?: boolean;
  showJobNameEditIcon?: boolean;
  showEditIcon?: boolean;
  showDeleteIcon?: boolean;
  showCancelIcon?: boolean;
  onDeleteClick?: () => void;
  addOTJ?: boolean;
  showButton?: boolean;
  showConversationIcon?: boolean;
  showEmailSubscriptionIcon?: boolean;
  onEmailSubscriptionClick?: () => void;
  onConversationButtonClick?: () => void;
  onEditJobNameButtonClick?: () => void;
  showConversationTitle?: () => string;
  onCancelClick?: () => void;
  name?: string;
  viewDetails?: boolean;
  onViewDetailsClick?: () => void;
  isQuoteDetailsLoading?: boolean;
  viewAllJobs?: boolean;
}

/**
 * @returns Page Title
 */

const PageTitle = ({
  title,
  Loader,
  showJobIcon,
  showJobNameEditIcon,
  showEditIcon,
  showDeleteIcon,
  onDeleteClick,
  showButton,
  addOTJ,
  showCancelIcon,
  showConversationIcon,
  showEmailSubscriptionIcon,
  onEmailSubscriptionClick,
  onConversationButtonClick,
  onEditJobNameButtonClick,
  showConversationTitle,
  onCancelClick,
  name,
  viewDetails,
  onViewDetailsClick,
  isQuoteDetailsLoading,
  viewAllJobs,
}: IPageTitleProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const processData = getProcessData();
  const canCreateOTJJob = processData?.some(
    (process: { processId: string | number }) => String(process?.processId) === USER_PROCESSES.OTJ
  );

  function handleCreateJobClick(): void {
    navigate('/createOneTimeJob');
  }

  return (
    <Container>
      <TitleWrapper>
        <div>
          <Icon
            onClick={() => {
              if (showCancelIcon) {
                onCancelClick?.();
              } else {
                navigate(-1);
              }
            }}
            src={Icons.LeftArrowIcon}
            alt={t('altTexts:backArrow')}
          />
          {Loader ? (
            <CircularProgress size={24} sx={{ color: theme.palette.primary.dark, marginRight: '0.5rem' }} />
          ) : (
            <>
              <Title>{title}</Title>
              {name && (
                <SubTitle>
                  {'- '}
                  {name}
                </SubTitle>
              )}
            </>
          )}
          {showJobNameEditIcon && (
            <Icon
              sx={{ width: '1.3rem', height: '1.3rem' }}
              onClick={onEditJobNameButtonClick}
              src={Icons.EditBlack}
              alt={t('altTexts:modify')}
            />
          )}
        </div>
        <div>
          {showJobIcon && <Icon onClick={handleCreateJobClick} src={Icons.CreateJobIcon} alt={t('altTexts:addIcon')} />}
          {showEditIcon && (
            <Link
              to="/createOneTimeJob"
              state={{ jobRecord: location.state?.facilityItem, isJobCreation: false, mediaFileIds: [] }}>
              <img src={Icons.EditBlack} alt={t('altTexts:modify')} />
            </Link>
          )}
          {showConversationIcon && onConversationButtonClick && hasAccessToConversations() && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #006B6B',
                borderRadius: '14px',
                padding: '10px 14px',
                cursor: 'pointer',
                width: '100%',
              }}
              onClick={onConversationButtonClick}>
              <img
                src={Icons.GreenConversationIcon}
                alt={t('altTexts:conversation')}
                style={{ width: '1.5rem', height: '1.5rem', marginRight: '8px', background: 'white' }}
              />
              <ConversationName style={{ color: '#006B6B' }}>
                {showConversationTitle ? showConversationTitle() : ''}
              </ConversationName>
            </Box>
          )}
          {viewDetails && onViewDetailsClick && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #006B6B',
                borderRadius: '14px',
                padding: '10px 14px',
                cursor: 'pointer',
                width: '100%',
                opacity: isQuoteDetailsLoading ? '0.5' : '1',
              }}
              onClick={onViewDetailsClick}>
              <ConversationName style={{ color: theme.palette.primary.dark }}>{'View Quote Details'}</ConversationName>
              {isQuoteDetailsLoading && (
                <CircularProgress size={24} sx={{ color: theme.palette.primary.dark, marginLeft: '1rem' }} />
              )}
            </Box>
          )}
          {showDeleteIcon && onDeleteClick && (
            <Icon src={Icons.DeleteIcon} alt={t('altTexts:delete')} onClick={onDeleteClick} />
          )}
          {showEmailSubscriptionIcon && (
            <Icon src={Icons.EmailIcon} alt={t('altTexts:emailSubscription')} onClick={onEmailSubscriptionClick} />
          )}
          {/* <Link to="/createOneTimeJob" style={{ textDecoration: 'none' }}>
            <Button
              icon={<img src={addIcon} alt={t('altTexts:addIcon')} />}
              type={ButtonType.Primary}
              label="One time job"
              iconDirection={IconDirection.Right}
            />
          </Link> */}
          {showCancelIcon && onCancelClick && (
            <Icon src={Icons.CancelIcon} alt={t('assignJob:cancel')} onClick={onCancelClick} />
          )}
          {showButton
            ? (addOTJ ?? canCreateOTJJob) && (
                <Link to="/createOneTimeJob" style={{ textDecoration: 'none' }}>
                  <Button
                    onClick={() => {
                      localStorage.removeItem(AsyncStorageKeys.currentJob);
                    }}
                    icon={<img src={Icons.PlusWhiteIcon} alt={t('altTexts:addIcon')} />}
                    type={ButtonType.Primary}
                    label={t('homePage:oneTimeJob')}
                    iconDirection={IconDirection.Right}
                  />
                </Link>
              )
            : null}
          {viewAllJobs && (
            <Link state={{ selectedDate: new Date() }} to={Paths.ListView} style={{ textDecoration: 'none' }}>
              <Button
                type={ButtonType.Secondary}
                label={t('dashboard:viewAllJobs')}
                icon={<img src={Icons.ArrowForwardIcon} alt={t('altTexts:rightArrow')} />}
                iconDirection={IconDirection.Right}
              />
            </Link>
          )}
        </div>
      </TitleWrapper>
    </Container>
  );
};

PageTitle.defaultProps = {
  showJobIcon: false,
  showEditIcon: false,
  showDeleteIcon: false,
};

export default PageTitle;
